import React from 'react';
import { motion } from 'framer-motion';
import { 
  HeartHandshake, 
  Rocket, 
  Repeat, 
  Layers, 
  Cpu, 
  ArrowUpRightFromSquare 
} from 'lucide-react';

const WhyChooseUs = () => {
  const reasons = [
    {
      icon: HeartHandshake,
      title: "Pain Relief",
      description: "We take the stress of financial management off your shoulders, so you can concentrate on growth."
    },
    {
      icon: Rocket,
      title: "Start Moving Forward from Day 1",
      description: "Our immediate support ensures you're ready to scale from the moment you partner with us."
    },
    {
      icon: Repeat,
      title: "Accountancy-as-a-Service",
      description: "Enjoy ongoing financial expertise through our flexible, subscription-based model."
    },
    {
      icon: Layers,
      title: "Full-stack Finance Function",
      description: "From accountancy to strategic planning, we offer a wide range of services in one location."
    },
    {
      icon: Cpu,
      title: "Industry-standard Tech Stack",
      description: "We leverage cutting-edge financial tools for efficiency, accuracy, and scalability."
    },
    {
      icon: ArrowUpRightFromSquare,
      title: "Planned Obsolescence",
      description: "We empower you to build internal financial capabilities, reducing dependency on us over time."
    }
  ];

  return (
    <section id="why-choose-us" className="py-24 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-[#e2e8f0]/40"
            style={{
              width: `${Math.random() * 80 + 20}px`,
              height: `${Math.random() * 80 + 20}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -15, 0],
              x: [0, 10, 0],
            }}
            transition={{
              duration: 15 + Math.random() * 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold text-gray-800 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Why Choose Us
          </motion.h2>
          
          <motion.p 
            className="text-xl text-gray-600 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Here's why AAIDAN K stands out as your ideal financial partner.
          </motion.p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, index) => (
            <motion.div 
              key={index}
              className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col h-full"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              whileHover={{ 
                y: -10,
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
              }}
            >
              <div className="flex items-start mb-6">
                <div className="bg-red-50 p-3 rounded-xl flex-shrink-0">
                  <reason.icon className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 ml-4 mt-1">{reason.title}</h3>
              </div>
              <p className="text-gray-600 flex-grow">{reason.description}</p>
            </motion.div>
          ))}
        </div>
        
        {/* Decorative elements */}
        <motion.div 
          className="absolute top-20 left-0 w-64 h-64 rounded-full bg-red-100/30 blur-3xl z-0"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        <motion.div 
          className="absolute bottom-20 right-0 w-56 h-56 rounded-full bg-blue-100/30 blur-3xl z-0"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>
    </section>
  );
};

export default WhyChooseUs;